import * as React from 'react'
import { connect } from 'react-redux'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { noop } from 'lodash'

import Button from '@doordash/component-button'
import { TextField } from '@doordash/component-fields'
import { Alert } from '@doordash/component-modal'
import NavBar from '@doordash/component-navbar'
import Separator from '@doordash/component-separator'
import { Text, Colors, Inset, Stack } from '@doordash/design-language'

import * as actions from '../../../../actions'
import {
  DeliveryIssueDispute,
  DeliveryIssue,
  PolicyViolationPages,
} from '../../../../models/types'
import routes from '../../../../routes'
import {
  getDasherId,
  hasSubmittedDisputeSelector,
} from '../../../../selectors/deliveryIssues'
import t from './strings'
import { WarningBadge, Wrapper } from './styles'
import { NavBarHeaderTitle } from '../components/styles'
import * as events from '../../../../events'
import { PolicyViolation, EventActions } from '../../../../typeModules/events'

type DeliveryDisputeProps = {
  issueId: string
  dasherId: string
  submitDeliveryIssueDispute: (
    dasherId: string,
    issueId: string,
    message: string
  ) => void
  deliveryIssueDetails: DeliveryIssue
  getDeliveryIssueDetails: (dasherId: string, issueId: string) => void
  deliveryIssueDispute: DeliveryIssueDispute
  clearDeliveryIssueDispute: () => void
  hasSubmittedDispute: boolean
  track: (data: PolicyViolation) => Promise<any>
}

/**
 * Delivery Issue Dispute webview component.
 * Allows a user to submit a dispute with text details.
 */
const DeliveryDispute: React.FunctionComponent<DeliveryDisputeProps &
  RouteComponentProps> = ({
  clearDeliveryIssueDispute,
  dasherId,
  deliveryIssueDispute,
  deliveryIssueDetails,
  getDeliveryIssueDetails,
  hasSubmittedDispute,
  history,
  issueId,
  submitDeliveryIssueDispute,
  track,
}) => {
  const [disputeMessage, setDisputeMessage] = React.useState('')
  const [showAlert, setAlert] = React.useState(false)
  const [trackSubmit, setTrackSubmit] = React.useState(false)
  const MAX_DISPUTE_LENGTH = 500
  const BUTTON_SUCCESS = 'SUCCESS'
  const BUTTON_FAILURE = 'ERROR'

  // Fetch details of an issue by dasher ID and issue ID
  React.useEffect(() => {
    getDeliveryIssueDetails(dasherId, issueId)
  }, [])

  // This prevents double counting on the
  // submission tracking click caused
  // by the animation end which is used to indicate
  // if its a success/error
  React.useEffect(() => {
    if (trackSubmit) {
      let result

      if (deliveryIssueDispute.isSubmitted) {
        result = EventActions.Success
      }
      if (deliveryIssueDispute.error) {
        result = EventActions.Error
      }

      track({
        type: deliveryIssueDetails.issueType,
        page: PolicyViolationPages.Dispute,
        action: EventActions.Submit,
        data: {
          issueId: deliveryIssueDetails.id,
          result,
        },
      })
    }
  }, [trackSubmit])

  // Submit button handler
  const submitDispute = () => {
    submitDeliveryIssueDispute(dasherId, issueId, disputeMessage)
  }

  // Get submit button state depending on the status of the dispute
  const getButtonState = () => {
    if (deliveryIssueDispute.isSubmitting) {
      return Button.States.Loading
    }
    if (deliveryIssueDispute.isSubmitted) {
      return Button.States.Success
    }
    if (deliveryIssueDispute.error) {
      return Button.States.Error
    }
    return Button.States.Default
  }

  /**
   * redirect the user back to the detail view if they have already submitted
   * a dispute
   */
  if (hasSubmittedDispute) {
    return (
      <Redirect
        to={`${routes.deliveryIssueDetail.buildPath({
          id: issueId,
        })}`}
      />
    )
  }
  React.useEffect(() => {
    track({
      type: deliveryIssueDetails.issueType,
      page: PolicyViolationPages.Dispute,
      action: EventActions.Rendered,
      data: {
        issueId: deliveryIssueDetails.id,
      },
    })
  }, [])

  return (
    <React.Fragment>
      <Wrapper>
        <div>
          <NavBar
            navigationType={NavBar.NavigationTypes.Close}
            onOverflowClick={noop}
            isSeparatorVisible={false}
            onNavigationClick={() => {
              history.goBack()
            }}
          />
          <NavBarHeaderTitle>
            <Text styles={Text.Styles.Bold24}>{t.titleText()}</Text>
          </NavBarHeaderTitle>
          <Inset>
            <Text>{t.titleDescriptionText()}</Text>
          </Inset>
          <Separator />
          <Inset>
            <TextField
              label={
                <Text styles={Text.Styles.Bold18}>
                  {t.textFieldLabelText()}
                </Text>
              }
              description={
                <WarningBadge>{t.textFieldDescriptionText()}</WarningBadge>
              }
              isMultiline={4}
              isResizeable={false}
              onChange={value => {
                setDisputeMessage(value)
              }}
              placeholder={t.textFieldPlaceholderText()}
              maxLength={MAX_DISPUTE_LENGTH}
              value={disputeMessage}
              autoFocus={true}
            />
            <Stack />
            <Text
              color={Colors.SystemGrey60}
              textAlign={Text.TextAlignments.Left}
            >
              {t.remainingCharactersText(
                MAX_DISPUTE_LENGTH - disputeMessage.length
              )}
            </Text>
          </Inset>
        </div>
        <div>
          <Inset>
            <Button
              type={Button.Types.PrimaryPill}
              isDisabled={disputeMessage.length === 0}
              onClick={submitDispute}
              state={getButtonState()}
              onAnimationEnd={status => {
                if (status === BUTTON_SUCCESS) {
                  setTrackSubmit(true)
                  clearDeliveryIssueDispute()
                  history.push({
                    pathname: `${routes.deliveryIssueDetail.buildPath({
                      id: issueId,
                    })}`,
                    search: '?submitted_dispute=true',
                  })
                } else if (status === BUTTON_FAILURE) {
                  setTrackSubmit(true)
                  setAlert(true)
                }
              }}
            >
              {t.buttonSubmitText()}
            </Button>
            <Stack size={Stack.Sizes.Large} />
          </Inset>
        </div>
        {showAlert ? (
          <Alert
            title={t.alertTitleText()}
            subtitle={t.alertSubtitleText()}
            dismissText={t.alertDismissText()}
            onDismiss={() => {
              setAlert(false)
              clearDeliveryIssueDispute()
            }}
          />
        ) : null}
      </Wrapper>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  dasherId: getDasherId(state),
  deliveryIssueDispute: state.deliveryIssueDispute,
  deliveryIssueDetails: state.deliveryIssueDetails,
  hasSubmittedDispute: hasSubmittedDisputeSelector(state),
})

const mapDispatchToProps = {
  getDeliveryIssueDetails: actions.getDeliveryIssueDetails,
  submitDeliveryIssueDispute: actions.submitDeliveryIssueDispute,
  clearDeliveryIssueDispute: actions.clearDeliveryIssueDispute,
  track: events.trackPolicyViolations,
}
export const DisconnectedDeliveryDispute = DeliveryDispute
export default connect(mapStateToProps, mapDispatchToProps)(DeliveryDispute)
